import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Drawer, LinearProgress } from "@octopusdeploy/design-system-components";
import { Avatar } from "@octopusdeploy/design-system-octopus-components";
import { space, text } from "@octopusdeploy/design-system-tokens";
import { logger } from "@octopusdeploy/logging";
import { useInlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { ActionTemplateSearchResource } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { ProcessTemplateProcessPreview } from "~/areas/projects/components/Process/ProcessTemplates/ProcessTemplateProcessPreview";
import { getVersionMask, UpdateBehaviour } from "~/areas/projects/components/Templates/TemplateVersioningSection";
import Markdown from "~/components/Markdown/index";
interface ProcessTemplatePreviewDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    template: ActionTemplateSearchResource;
    availableVersions: string[];
}
export function ProcessTemplatePreviewDrawer({ isOpen, onClose, template, availableVersions }: ProcessTemplatePreviewDrawerProps) {
    const { result: processTemplate, isLoading } = useInlineStatusQuery(async (repository) => {
        if (!template.Version) {
            logger.warn("Template version is missing. This should never happen");
            return null;
        }
        const updateBehaviour = template.Prerelease ? UpdateBehaviour.PreRelease : UpdateBehaviour.Minor;
        const versionMask = getVersionMask(template.Version, updateBehaviour);
        return await repository.ProcessTemplates.getByVersionMask(template.Id, versionMask);
    }, [template.Id, template.Prerelease, template.Version], "getProcessTemplate");
    return (<Drawer isOpen={isOpen} onClose={onClose} title={template.Name} subtitle="Process Template" avatar={<Avatar type={"template"} size={"large"} src={template.Links.Logo} alt={"template icon"}/>} variant="customActions" layout="basic" customActions={[<ActionButton key="close" onClick={onClose} type={ActionButtonType.Secondary} label="Close"/>]} width="narrow">
            <LinearProgress variant={"indeterminate"} show={isLoading}/>
            <div className={styles.outerContainer}>
                {template.Description && (<div>
                        <Markdown markup={template.Description}/>
                    </div>)}
                <div className={styles.versionContainer}>
                    <h2 className={styles.title}>Versions</h2>
                    <ul className={styles.list}>
                        {availableVersions.map((version) => (<li key={version}>{version}</li>))}
                    </ul>
                </div>
                {processTemplate?.ProcessTemplate.Steps && (<div className={styles.processContainer}>
                        <h2 className={styles.title}>Process</h2>
                        <ProcessTemplateProcessPreview steps={processTemplate.ProcessTemplate.Steps}/>
                    </div>)}
            </div>
        </Drawer>);
}
const styles = {
    title: css({
        font: text.heading.small,
        margin: 0,
    }),
    list: css({
        font: text.body.regular.large,
        listStyleType: "disc",
        margin: 0,
        paddingLeft: space[16],
    }),
    outerContainer: css({
        display: "flex",
        padding: space[16],
        flexDirection: "column",
        gap: space[24],
    }),
    versionContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[12],
    }),
    processContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[12],
    }),
};
